<script setup lang="ts">
/**
 * NavBar Component - 導航列元件
 * 
 * Displays the main navigation bar with:
 * - App title: 錦德報關行系統
 * - Current user's display name
 * - Navigation links (based on permissions)
 * - Logout button
 * 
 * Uses Vue reactive state to control dropdowns instead of Bootstrap JS
 * to avoid conflicts with other components.
 * 
 * Requirements: 12.8
 */
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

// Dropdown state - Vue controlled
const openDropdown = ref<string | null>(null);
const mobileMenuOpen = ref(false);

// Close dropdown when route changes
watch(() => route.path, () => {
  openDropdown.value = null;
});

// Toggle dropdown
function toggleDropdown(name: string) {
  if (openDropdown.value === name) {
    openDropdown.value = null;
  } else {
    openDropdown.value = name;
  }
}

// Check if dropdown is open
function isDropdownOpen(name: string): boolean {
  return openDropdown.value === name;
}

// Close dropdown when clicking outside
function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (!target.closest('.dropdown')) {
    openDropdown.value = null;
  }
}

// Toggle mobile menu
function toggleMobileMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value;
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});

// Computed properties
const displayName = computed(() => authStore.displayName);
const isAdmin = computed(() => authStore.isAdmin);
const isLoading = computed(() => authStore.isLoading);

// Check if a route is active
function isActiveRoute(path: string): boolean {
  return route.path.startsWith(path);
}

function isExactRoute(path: string): boolean {
  return route.path === path;
}

// Handle logout
async function handleLogout() {
  openDropdown.value = null;
  try {
    await authStore.logout();
    router.push('/login');
  } catch (error) {
    console.error('Logout failed:', error);
    router.push('/login');
  }
}
</script>

<template>
  <nav class="navbar navbar-expand-lg navbar-dark bg-primary">
    <div class="container-fluid">
      <!-- Brand -->
      <router-link class="navbar-brand d-flex align-items-center" to="/declarations">
        <i class="bi bi-building me-2"></i>
        <span>錦德報關行系統</span>
      </router-link>

      <!-- Mobile Toggle Button -->
      <button
        class="navbar-toggler"
        type="button"
        @click="toggleMobileMenu"
        aria-controls="navbarNav"
        :aria-expanded="mobileMenuOpen"
        aria-label="Toggle navigation"
      >
        <span class="navbar-toggler-icon"></span>
      </button>

      <!-- Navigation Links -->
      <div 
        class="navbar-collapse" 
        :class="{ 'collapse': !mobileMenuOpen, 'show': mobileMenuOpen }"
        id="navbarNav"
      >
        <ul class="navbar-nav me-auto">
          <!-- 報單管理 - All users -->
          <li class="nav-item dropdown" :class="{ 'show': isDropdownOpen('declaration') }">
            <a
              class="nav-link dropdown-toggle"
              :class="{ active: isActiveRoute('/declarations') || isActiveRoute('/archives') }"
              href="#"
              role="button"
              @click.prevent="toggleDropdown('declaration')"
              :aria-expanded="isDropdownOpen('declaration')"
            >
              <i class="bi bi-file-earmark-text me-1"></i>
              報單管理
            </a>
            <ul class="dropdown-menu" :class="{ 'show': isDropdownOpen('declaration') }">
              <li>
                <router-link
                  class="dropdown-item"
                  :class="{ active: isActiveRoute('/declarations') }"
                  to="/declarations"
                  @click="openDropdown = null"
                >
                  <i class="bi bi-list-ul me-1"></i>
                  報單清單
                </router-link>
              </li>
              <li>
                <router-link
                  class="dropdown-item"
                  :class="{ active: isActiveRoute('/archives') }"
                  to="/archives"
                  @click="openDropdown = null"
                >
                  <i class="bi bi-archive me-1"></i>
                  封存報單
                </router-link>
              </li>
            </ul>
          </li>

          <!-- 訂單管理 - Admin only -->
          <li v-if="isAdmin" class="nav-item dropdown" :class="{ 'show': isDropdownOpen('order') }">
            <a
              class="nav-link dropdown-toggle"
              :class="{ active: isActiveRoute('/orders') || isActiveRoute('/customers') }"
              href="#"
              role="button"
              @click.prevent="toggleDropdown('order')"
              :aria-expanded="isDropdownOpen('order')"
            >
              <i class="bi bi-box-seam me-1"></i>
              訂單管理 <span class="badge bg-warning text-dark ms-1" style="font-size: 0.6em; vertical-align: middle;">Beta</span>
            </a>
            <ul class="dropdown-menu" :class="{ 'show': isDropdownOpen('order') }">
              <li>
                <router-link
                  class="dropdown-item"
                  :class="{ active: isExactRoute('/orders') }"
                  to="/orders"
                  @click="openDropdown = null"
                >
                  <i class="bi bi-list-ul me-1"></i>
                  訂單清單
                </router-link>
              </li>
              <li>
                <router-link
                  class="dropdown-item"
                  :class="{ active: isActiveRoute('/orders/archives') }"
                  to="/orders/archives"
                  @click="openDropdown = null"
                >
                  <i class="bi bi-archive me-1"></i>
                  封存訂單
                </router-link>
              </li>
              <li><hr class="dropdown-divider"></li>
              <li>
                <router-link
                  class="dropdown-item"
                  :class="{ active: isActiveRoute('/customers') }"
                  to="/customers"
                  @click="openDropdown = null"
                >
                  <i class="bi bi-people-fill me-1"></i>
                  客戶管理
                </router-link>
              </li>
            </ul>
          </li>

          <!-- 報價系統 - Admin only -->
          <li v-if="isAdmin" class="nav-item dropdown" :class="{ 'show': isDropdownOpen('quotation') }">
            <a
              class="nav-link dropdown-toggle"
              :class="{ active: isActiveRoute('/quotation') || isActiveRoute('/equipments') }"
              href="#"
              role="button"
              @click.prevent="toggleDropdown('quotation')"
              :aria-expanded="isDropdownOpen('quotation')"
            >
              <i class="bi bi-calculator me-1"></i>
              報價系統
            </a>
            <ul class="dropdown-menu" :class="{ 'show': isDropdownOpen('quotation') }">
              <li>
                <router-link
                  class="dropdown-item"
                  :class="{ active: isActiveRoute('/quotation') }"
                  to="/quotation"
                  @click="openDropdown = null"
                >
                  <i class="bi bi-search me-1"></i>
                  報價查詢
                </router-link>
              </li>
              <li>
                <router-link
                  class="dropdown-item"
                  :class="{ active: isActiveRoute('/equipments') }"
                  to="/equipments/dashboard"
                  @click="openDropdown = null"
                >
                  <i class="bi bi-gear me-1"></i>
                  配件管理
                </router-link>
              </li>
            </ul>
          </li>

          <!-- 管理介面 - Admin only -->
          <li v-if="isAdmin" class="nav-item dropdown" :class="{ 'show': isDropdownOpen('admin') }">
            <a
              class="nav-link dropdown-toggle"
              :class="{ active: isActiveRoute('/users') }"
              href="#"
              role="button"
              @click.prevent="toggleDropdown('admin')"
              :aria-expanded="isDropdownOpen('admin')"
            >
              <i class="bi bi-sliders me-1"></i>
              管理介面
            </a>
            <ul class="dropdown-menu" :class="{ 'show': isDropdownOpen('admin') }">
              <li>
                <router-link
                  class="dropdown-item"
                  :class="{ active: isActiveRoute('/users') }"
                  to="/users"
                  @click="openDropdown = null"
                >
                  <i class="bi bi-people me-1"></i>
                  使用者管理
                </router-link>
              </li>
            </ul>
          </li>
        </ul>

        <!-- User Info & Logout -->
        <ul class="navbar-nav">
          <li class="nav-item dropdown" :class="{ 'show': isDropdownOpen('user') }">
            <a
              class="nav-link dropdown-toggle d-flex align-items-center"
              href="#"
              role="button"
              @click.prevent="toggleDropdown('user')"
              :aria-expanded="isDropdownOpen('user')"
            >
              <i class="bi bi-person-circle me-1"></i>
              <span>{{ displayName }}</span>
            </a>
            <ul class="dropdown-menu dropdown-menu-end" :class="{ 'show': isDropdownOpen('user') }">
              <li>
                <span class="dropdown-item-text text-muted">
                  <small>登入身份：{{ displayName }}</small>
                </span>
              </li>
              <li><hr class="dropdown-divider"></li>
              <li>
                <button
                  class="dropdown-item text-danger"
                  @click="handleLogout"
                  :disabled="isLoading"
                >
                  <i class="bi bi-box-arrow-right me-1"></i>
                  {{ isLoading ? '登出中...' : '登出' }}
                </button>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  </nav>
</template>

<style scoped>
.navbar {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1030;
}

.navbar-brand {
  font-weight: 600;
}

.nav-link {
  transition: opacity 0.2s ease;
}

.nav-link:hover {
  opacity: 0.9;
}

.nav-link.active {
  font-weight: 500;
}

/* Dropdown styles - Vue controlled */
.dropdown-menu {
  position: absolute;
  z-index: 1031;
}

.dropdown-menu.show {
  display: block;
}

.dropdown-item:hover {
  cursor: pointer;
}

.dropdown-item:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.dropdown-item.active {
  background-color: var(--bs-primary);
  color: white;
}

.nav-link.dropdown-toggle.active {
  font-weight: 500;
}
</style>
